<template>
  <div class="ui-email-chip-input-wrapper" v-click-outside="closeDropdown">
    <label :for="inputId" class="ui-email-chip-label" v-if="label" v-text="label" />
    <div class="chips-display" v-if="emails.length > 0">
      <div class="email-chip" v-for="(email, i) in emails" :key="i">
        <span class="chip-text" v-text="email" />
        <i class="fa-solid fa-xmark chip-remove" @click="removeEmail(i)" />
      </div>
    </div>
    <div class="input-box" :class="{ focused }" @click="focusInput">
      <input
        :id="inputId"
        ref="inputRef"
        v-model="rawInput"
        class="chip-input"
        :placeholder="t('admin_global_entermail')"
        @keydown="onKeydown"
        @input="onInput"
        @focus="onFocus"
        @blur="onBlur"
      />
      <span class="chip-box-icon">
        <i class="spin-icon fa-solid fa-spinner" v-if="searching" />
        <i class="fa-solid fa-magnifying-glass" v-else />
      </span>
    </div>
    <div class="search-dropdown" v-if="showDropdown">
      <div class="search-dropdown-loading" v-if="searching">
        <i class="spin-icon fa-solid fa-spinner" />
      </div>
      <template v-else>
        <div
          v-for="(user, i) in employeeList"
          :key="i"
          class="search-item"
          @mousedown.prevent="addFromUser(user)"
        >
          <div class="search-item-avatar">
            <img v-if="user.imageUrl" :src="user.imageUrl" :alt="user.name" />
            <i class="fa-solid fa-user" v-else />
          </div>
          <div class="search-item-info">
            <div class="search-item-name">{{ user.name }} {{ user.surname }}</div>
            <div class="search-item-sub">{{ user.email || '@' + user.profileName }}</div>
          </div>
          <i class="fa-solid fa-plus search-item-add" />
        </div>
        <div
          class="search-dropdown-empty"
          v-if="employeeList.length === 0"
          v-text="t('admin_global_noresults')"
        />
      </template>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { useEmployeeStore } from '@/pinia'
  import { Debounce, UseI18n } from '@/utils-ts'
  import { computed, ref, useId } from 'vue'

  const { label } = defineProps<{ label?: string }>()
  const model = defineModel<string | null>({ required: true })
  const { t } = UseI18n()
  const employeeStore = useEmployeeStore()
  const inputId = useId()

  const inputRef = ref<HTMLInputElement | null>(null)
  const rawInput = ref('')
  const focused = ref(false)
  const searching = ref(false)
  const showDropdown = ref(false)
  const employeeList = ref<any[]>([])

  const emails = computed<string[]>(() => {
    if (!model.value) return []
    return model.value
      .split(',')
      .map(e => e.trim())
      .filter(Boolean)
  })

  const updateModel = (list: string[]) => {
    model.value = list.length ? list.join(',') : null
  }

  const isValidEmail = (val: string) => {
    const trimmed = val.trim()
    if (/\s/.test(trimmed)) return false

    const at = trimmed.indexOf('@')
    if (at <= 0 || at !== trimmed.lastIndexOf('@')) return false

    const domain = trimmed.slice(at + 1)
    const dot = domain.lastIndexOf('.')
    return dot > 0 && dot < domain.length - 1
  }

  const addEmail = (email: string) => {
    const trimmed = email.trim()
    if (!trimmed || !isValidEmail(trimmed)) return
    if (emails.value.includes(trimmed)) return
    updateModel([...emails.value, trimmed])
  }

  const removeEmail = (index: number) => {
    const updated = [...emails.value]
    updated.splice(index, 1)
    updateModel(updated)
  }

  const addRawEmail = () => {
    if (!rawInput.value.trim()) return
    addEmail(rawInput.value)
    rawInput.value = ''
    employeeList.value = []
    showDropdown.value = false
  }

  const addFromUser = (user: any) => {
    if (user.email) {
      addEmail(user.email)
    }
    rawInput.value = ''
    showDropdown.value = false
    employeeList.value = []
  }

  const onKeydown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      addRawEmail()
    } else if (
      (e.key === 'Backspace' || e.key === 'Delete') &&
      !rawInput.value &&
      emails.value.length > 0
    ) {
      removeEmail(emails.value.length - 1)
    }
  }

  const focusInput = () => {
    inputRef.value?.focus()
  }

  const onFocus = () => {
    focused.value = true
  }

  const onBlur = () => {
    focused.value = false
    if (rawInput.value.trim() && isValidEmail(rawInput.value)) {
      addRawEmail()
    }
  }

  const closeDropdown = () => {
    showDropdown.value = false
  }

  const triggerSearch = Debounce(async () => {
    const keyword = rawInput.value.trim()
    if (!keyword) {
      showDropdown.value = false
      employeeList.value = []
      return
    }
    searching.value = true
    showDropdown.value = true
    const result = await employeeStore.GetEmployeeListByFilter({
      SearchKeyword: keyword || '*',
      Skip: 0,
      Take: 20,
    })
    employeeList.value = result
    searching.value = false
  }, 400)

  const onInput = () => triggerSearch()
</script>

<style lang="scss" scoped>
  .ui-email-chip-input-wrapper {
    width: 100%;
    position: relative;

    .ui-email-chip-label {
      @include column-label;
    }

    .chips-display {
      display: flex;
      flex-wrap: wrap;
      gap: 0.375rem;
      padding: 0.5rem 0.75rem;
      background-color: #f7f8fa;
      border: 1px solid $border-color;
      border-radius: 5px;
      margin-bottom: 0.375rem;

      .email-chip {
        display: inline-flex;
        align-items: center;
        gap: 0.375rem;
        background-color: $white;
        border: 1px solid $border-color;
        color: $primary-text-color;
        border-radius: 5px;
        padding: 0.2rem 0.6rem;
        font-size: 13px;
        max-width: 260px;

        .chip-text {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .chip-remove {
          cursor: pointer;
          font-size: 11px;
          color: #aaa;
          transition: color 0.2s;
          flex-shrink: 0;

          &:hover {
            color: $danger;
          }
        }
      }
    }

    .input-box {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: calc(0.5em - 1px) 1em;
      border: 1px solid $border-color;
      border-radius: 5px;
      background-color: $white;
      cursor: text;
      transition: 0.2s border-color ease;

      &.focused {
        border-color: $brandColor;
      }

      .chip-input {
        flex: 1;
        border: none;
        outline: none;
        font-size: 14px;
        color: $primary-text-color;
        background-color: transparent;

        &::placeholder {
          color: #ccc;
        }
      }

      .chip-box-icon {
        color: $primary-text-color;
        flex-shrink: 0;
      }
    }

    .search-dropdown {
      position: absolute;
      top: 100%;
      left: 0;
      width: 100%;
      background: $white;
      border-radius: 0 0 5px 5px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      z-index: 99999;
      max-height: 220px;
      overflow-y: auto;
      @include scroll-bar;

      .search-dropdown-loading,
      .search-dropdown-empty {
        padding: 1.25rem;
        display: flex;
        justify-content: center;
        align-items: center;
        font-size: 13px;
        color: #aaa;
      }

      .search-item {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        padding: 0.5rem 0.75rem;
        cursor: pointer;
        transition: background 0.15s;

        &:hover {
          background-color: #f5f5f5;

          .search-item-add {
            opacity: 1;
          }
        }

        .search-item-avatar {
          width: 32px;
          height: 32px;
          border-radius: 5px;
          background-color: $border-color;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          overflow: hidden;

          img {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }

          i {
            font-size: 16px;
            color: #a5a5a5;
          }
        }

        .search-item-info {
          flex: 1;
          overflow: hidden;

          .search-item-name {
            font-size: 13px;
            font-weight: 500;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .search-item-sub {
            font-size: 12px;
            color: #a5a5a5;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
        }

        .search-item-add {
          font-size: 13px;
          color: $brandColor;
          flex-shrink: 0;
          opacity: 0;
          transition: opacity 0.15s;
        }
      }
    }

    .spin-icon {
      animation: spin 0.75s linear infinite;
    }

    @keyframes spin {
      0% {
        transform: rotate(0deg);
      }
      100% {
        transform: rotate(360deg);
      }
    }
  }
</style>
